/**
 * usePortalDashboard Hook
 *
 * Story: 11-1-portal-de-acesso-externo
 * Task: Frontend - Portal Dashboard Page
 *
 * Manages portal dashboard data fetching with React Query
 */
import { useQuery } from "@tanstack/react-query";
import { portalDashboardApi } from "@/services/portalApi";
import type {
  DashboardSummary,
  ProcessosListResponse,
  ProcessosQueryParams,
  FinanceiroListResponse,
  FinanceiroQueryParams,
} from "@/types/portal";

/**
 * Hook to fetch dashboard summary metrics
 *
 * Includes:
 * - processos_ativos: Total active processes
 * - pendencias_proximas: Processes with action due within 7 days
 * - processos_em_andamento: In-progress processes
 * - saldo_pendente: Total pending payment amount
 * - total_pago_mes: Total paid this month
 * - vencimentos_proximos: Payments due within 7 days
 */
export function usePortalDashboard() {
  return useQuery({
    queryKey: ["portal-dashboard"],
    queryFn: async () => {
      return await portalDashboardApi.getDashboard();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - dashboard data doesn't change frequently
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    refetchOnWindowFocus: true, // Refetch when user returns to tab
  });
}

/**
 * Hook to fetch paginated list of processes
 *
 * @param params - Query parameters (page, page_size, status_filter)
 */
export function usePortalProcessos(params?: ProcessosQueryParams, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ["portal-processos", params],
    queryFn: async () => {
      return await portalDashboardApi.getProcessos(params);
    },
    // Tela "Processos" escondida em Administração › Telas do Portal → sem a
    // permissão, a busca daria 403 a cada visita ao Início.
    enabled: options?.enabled ?? true,
    staleTime: 3 * 60 * 1000, // 3 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to fetch paginated list of payments
 *
 * @param params - Query parameters (page, page_size)
 * @param options - { enabled } gate; pass `false` to skip the request entirely
 *   (e.g. partner_staff sem portal:financeiro:view → evita o 403 no console).
 */
export function usePortalFinanceiro(
  params?: FinanceiroQueryParams,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ["portal-financeiro", params],
    queryFn: async () => {
      return await portalDashboardApi.getFinanceiro(params);
    },
    enabled: options?.enabled ?? true,
    staleTime: 3 * 60 * 1000, // 3 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}
